import { DateInputField, FormattedNumberInputField } from "@/components/shared";
import { createDateSchema, postiveNumberZeroToOneHundred, stringToNumberOrUndefined } from "@/utils/form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { UseFormReturn } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { getInputWidth } from "../../Shared/helpers";
import { cn } from "@/lib/utils";

export const createContractSchema = (t: TFunction) => {
  return z.object({
    contract: z.object({
      startDate: createDateSchema(t),
      endDate: createDateSchema(t),
      paymentTerm: z.preprocess(
        stringToNumberOrUndefined,
        z.number().int().positive().optional()
      ),
      escalationClause: z.preprocess(
        stringToNumberOrUndefined,
        // Duplicate of postiveNumberZeroToOneHundred due putting optional after preprocess does give us the desired results
        // And can't put optional in the function due to it being generic and used in other places
        z.number({
          error: (issue) => issue.input === undefined ?
            `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.isrequired")}`
            :
            `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.benumber")}`,
        })
          .min(0, `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.atleast0")}`)
          .max(100, `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.atmax100")}`).optional()
      ),
      priceCriteria: postiveNumberZeroToOneHundred(t("newtender.formgroups.contract.inputs.price.label")),
      qualityCriteria: postiveNumberZeroToOneHundred(t("newtender.formgroups.contract.inputs.qualityconcept.label")),
      referenceCriteria: postiveNumberZeroToOneHundred(t("newtender.formgroups.contract.inputs.references.label")),
    }).check((ctx) => {
      if (ctx.value.endDate.getTime() < ctx.value.startDate.getTime()) {
        ctx.issues.push({
          code: "custom",
          path: ["endDate"],
          message: t("schemas:createtender.contract.enddateafterstartdate"),
          input: ctx.value.endDate,
        });
      }

      // sum of price, qualityConcept and references should be 100
      if (ctx.value.priceCriteria + ctx.value.qualityCriteria + ctx.value.referenceCriteria !== 100) {
        ctx.issues.push({
          code: "custom",
          origin: "array",
          message: t("schemas:createtender.contract.sumofawardingcriteria"),
          input: ctx.value.priceCriteria + ctx.value.qualityCriteria + ctx.value.referenceCriteria,
        });
      }
    }),
  });
};

export const contractDefaultValues = {
  contract: {
    startDate: undefined,
    endDate: undefined,
    paymentTerm: undefined,
    escalationClause: undefined,
    priceCriteria: undefined,
    qualityCriteria: undefined,
    referenceCriteria: undefined,
  }
};

export function ContractFormGroup({ form, leftPanelWidth }: { form: UseFormReturn<any>, leftPanelWidth: number }) {
  const { t } = useTranslation();

  return (
    <div>
      <div id="contract" className="form-field-container">
        {/* Start Date */}
        <DateInputField
          form={form}
          name="contract.startDate"
          label={t("newtender.formgroups.contract.inputs.startdate.label")}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        {/* End Date */}
        <DateInputField
          form={form}
          name="contract.endDate"
          label={t("newtender.formgroups.contract.inputs.enddate.label")}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        {/* Payment Term */}
        <FormattedNumberInputField
          control={form.control}
          name="contract.paymentTerm"
          label={t("newtender.formgroups.contract.inputs.paymentterm.label")}
          placeholder={t("newtender.formgroups.contract.inputs.paymentterm.placeholder")}
          additionalSuffix={t("newtender.formgroups.contract.inputs.paymentterm.suffix")}
          isInteger
          isStandalone
          showOptional
          style="decimal"
          spaceBetweenSuffix={true}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        {/* Escalation Clause */}
        <FormattedNumberInputField
          control={form.control}
          name="contract.escalationClause"
          label={t("newtender.formgroups.contract.inputs.escalationclause.label")}
          placeholder={t("newtender.formgroups.contract.inputs.escalationclause.placeholder")}
          additionalSuffix="%"
          isStandalone
          showOptional
          style="decimal"
          className={cn(getInputWidth(leftPanelWidth))}
        />

        <Card className={cn("flex flex-col gap-2 p-4 form-item", getInputWidth(leftPanelWidth))}>
          <Label>{t("newtender.formgroups.contract.awardingcriteria")}</Label>
          <p className="text-xs text-slate-400">{t("newtender.formgroups.contract.awardingcriteriadesc")}</p>
          <FormattedNumberInputField
            control={form.control}
            name="contract.priceCriteria"
            label={t("newtender.formgroups.contract.inputs.price.label")}
            placeholder={t("newtender.formgroups.contract.inputs.price.placeholder")}
            additionalSuffix="%"
            isStandalone
            style="decimal"
            className="w-full"
          />
          <FormattedNumberInputField
            control={form.control}
            name="contract.qualityCriteria"
            label={t("newtender.formgroups.contract.inputs.qualityconcept.label")}
            placeholder={t("newtender.formgroups.contract.inputs.qualityconcept.placeholder")}
            additionalSuffix="%"
            isStandalone
            style="decimal"
            className="w-full"
          />
          <FormattedNumberInputField
            control={form.control}
            name="contract.referenceCriteria"
            label={t("newtender.formgroups.contract.inputs.references.label")}
            placeholder={t("newtender.formgroups.contract.inputs.references.placeholder")}
            additionalSuffix="%"
            isStandalone
            style="decimal"
            className="w-full"
          />
          {
            form.formState.errors.contract && (
              <p className="input-error-message">
                {form.formState.errors.contract.message as string}
              </p>
            )
          }
        </Card>
      </div>
    </div>
  );
}
